import { POINTS_OF_SAIL, type PointOfSail } from "@nav-games/sailing";

/// Points-of-sail compass: a circular figure with the wind arrow at
/// the top, six sectors radiating from the centre, and a boat
/// silhouette drawn at the heading of interest.
///
/// In the Reference panel `highlightAngleDeg` is null and all
/// sectors are labelled. In the game the boat is drawn at a random
/// heading and the student picks the point of sail.

interface PointsOfSailFigureProps {
  /// Heading off the true wind in degrees [0, 180]. 0 = head to
  /// wind ("in irons"), 180 = dead downwind ("run"). null hides the
  /// boat.
  highlightAngleDeg?: number | null;
  /// Which sector to draw filled — used in the reveal to show the
  /// correct band even if the boat isn't shown.
  highlightPoint?: PointOfSail | null;
  /// All sectors labelled with their name — used in Reference.
  showAllLabels?: boolean;
  /// Render the figure mirrored (port-tack heading) instead of the
  /// default starboard-tack right side.
  port?: boolean;
  size?: number;
}

const VW = 320;
const VH = 320;
const CX = VW / 2;
const CY = VH / 2;
const R = 130;
const TEXT = "var(--text, #0f172a)";
const MUTED = "var(--text-muted, #64748b)";
const SECTOR_FILL = "rgba(59, 130, 246, 0.08)";
const SECTOR_FILL_NOGO = "rgba(184, 48, 42, 0.10)";
const ACCENT = "#c0382b";

/// Convert "angle off the wind in degrees" to an SVG angle. Wind
/// blows DOWN (from top of the figure). At angleOffWind = 0 the boat
/// points UP, into the wind. Increasing angle rotates clockwise on
/// the starboard side (right of the figure).
function svgAngleRad(angleOffWindDeg: number, port: boolean): number {
  const sign = port ? -1 : 1;
  // Up is -PI/2 in SVG; clockwise rotation = positive.
  return -Math.PI / 2 + sign * (angleOffWindDeg * Math.PI) / 180;
}

function pointOnCircle(
  angleOffWindDeg: number,
  radius: number,
  port: boolean,
): { x: number; y: number } {
  const a = svgAngleRad(angleOffWindDeg, port);
  return { x: CX + Math.cos(a) * radius, y: CY + Math.sin(a) * radius };
}

function sectorPath(
  fromDeg: number,
  toDeg: number,
  port: boolean,
): string {
  const a = pointOnCircle(fromDeg, R, port);
  const b = pointOnCircle(toDeg, R, port);
  const largeArc = Math.abs(toDeg - fromDeg) > 180 ? 1 : 0;
  const sweep = port ? 0 : 1;
  return `M ${CX} ${CY} L ${a.x} ${a.y} A ${R} ${R} 0 ${largeArc} ${sweep} ${b.x} ${b.y} Z`;
}

function BoatSilhouette({
  angleOffWindDeg,
  port,
}: {
  angleOffWindDeg: number;
  port: boolean;
}) {
  const a = svgAngleRad(angleOffWindDeg, port);
  const deg = (a * 180) / Math.PI + 90; // pointing in heading direction
  return (
    <g
      transform={`translate(${CX} ${CY}) rotate(${deg})`}
      aria-label="Boat silhouette at the chosen heading"
    >
      {/* Hull — pointed teardrop. */}
      <path
        d="M 0 -28 Q 10 0 8 22 L -8 22 Q -10 0 0 -28 Z"
        fill="#1e293b"
        stroke="white"
        strokeWidth={1}
      />
      {/* Mast + sail (boom rotated to match the apparent-wind side). */}
      <line x1={0} y1={-12} x2={0} y2={20} stroke="white" strokeWidth={1.5} />
      <path
        d={`M 0 -10 Q ${port ? -14 : 14} 6 ${port ? -2 : 2} 18 Z`}
        fill="#fde68a"
        stroke="white"
        strokeWidth={0.8}
      />
    </g>
  );
}

export function PointsOfSailFigure({
  highlightAngleDeg = null,
  highlightPoint = null,
  showAllLabels = false,
  port = false,
  size = VW,
}: PointsOfSailFigureProps) {
  return (
    <svg
      viewBox={`0 0 ${VW} ${VH}`}
      width={size}
      height={(size * VH) / VW}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label="Points-of-sail compass"
      style={{ maxWidth: "100%", height: "auto", display: "block" }}
    >
      {/* Outer circle. */}
      <circle
        cx={CX}
        cy={CY}
        r={R}
        fill="var(--bg-elev, #f8fafc)"
        stroke={MUTED}
        strokeWidth={1}
      />

      {/* Sector fills for each point of sail. Mirror on the port
          side so the figure reads symmetrically. */}
      {POINTS_OF_SAIL.map((p) => {
        const lo = p.midAngleDeg - p.halfWidthDeg;
        const hi = p.midAngleDeg + p.halfWidthDeg;
        const isNoGo = p.id === "in-irons";
        const isFocus = highlightPoint === p.id;
        const fill = isFocus
          ? "rgba(192, 56, 43, 0.18)"
          : isNoGo
            ? SECTOR_FILL_NOGO
            : SECTOR_FILL;
        // Render both sides (port + starboard) of every sector
        // except in-irons (which spans across the wind already).
        return (
          <g key={p.id}>
            <path d={sectorPath(lo, hi, port)} fill={fill} />
            {!isNoGo && (
              <path d={sectorPath(lo, hi, !port)} fill={fill} opacity={0.6} />
            )}
          </g>
        );
      })}

      {/* Sector dividers. */}
      {POINTS_OF_SAIL.map((p) => {
        const hi = p.midAngleDeg + p.halfWidthDeg;
        const a = pointOnCircle(hi, R, port);
        const b = pointOnCircle(hi, R, !port);
        return (
          <g key={`div-${p.id}`}>
            <line
              x1={CX}
              y1={CY}
              x2={a.x}
              y2={a.y}
              stroke={MUTED}
              strokeWidth={0.7}
            />
            <line
              x1={CX}
              y1={CY}
              x2={b.x}
              y2={b.y}
              stroke={MUTED}
              strokeWidth={0.7}
            />
          </g>
        );
      })}

      {/* Wind arrow — from top of the circle pointing down toward the
          centre, with a "TRUE WIND" caption above. */}
      <line
        x1={CX}
        y1={20}
        x2={CX}
        y2={CY - R - 8}
        stroke={TEXT}
        strokeWidth={2}
      />
      <polygon
        points={`${CX - 5},${CY - R - 8} ${CX + 5},${CY - R - 8} ${CX},${CY - R - 2}`}
        fill={TEXT}
      />
      <text
        x={CX}
        y={14}
        fontSize={11}
        textAnchor="middle"
        fill={TEXT}
        fontWeight={700}
      >
        TRUE WIND
      </text>

      {/* Sector labels — print at the mid-angle on both sides if
          showAllLabels, only on the focus side otherwise. */}
      {showAllLabels &&
        POINTS_OF_SAIL.map((p) => {
          const labelR = R - 30;
          const pos = pointOnCircle(p.midAngleDeg, labelR, port);
          const isFocus = highlightPoint === p.id;
          return (
            <text
              key={`lbl-${p.id}`}
              x={pos.x}
              y={pos.y + 3}
              fontSize={10}
              textAnchor="middle"
              fill={isFocus ? ACCENT : TEXT}
              fontWeight={isFocus ? 700 : 500}
            >
              {p.name.replace(" (no-go zone)", "")}
            </text>
          );
        })}

      {/* Boat silhouette at the highlight heading. */}
      {highlightAngleDeg !== null && highlightAngleDeg !== undefined && (
        <BoatSilhouette
          angleOffWindDeg={highlightAngleDeg}
          port={port}
        />
      )}
    </svg>
  );
}
